// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { Disposition, Severity, Tier } from './types';
import {
  abaChecksum,
  ibanMod97,
  jwtDecodable,
  luhn,
  ssnStructural,
  type ValidateResult,
} from './validators';

/**
 * The recogniser registry.
 *
 * The bar is PRECISION, not recall. A scanner that cries wolf gets turned off, and a scanner
 * that gets turned off protects nobody. Every rule here either validates a checksum, or matches
 * a prefix the vendor themselves publish, or is structural. Nothing is "a 32-character hex
 * string near the word token" — that is what the entropy heuristic is for, and the entropy
 * heuristic ships switched off.
 *
 * TWO SPANS, ALWAYS. `pattern` matches more than the secret on five rules, deliberately:
 * redacting match[0] on `url-embedded-credential` would destroy the scheme, user and host of a
 * DSN, and on `aba-routing` it would delete the words "routing number" from the corpus. So
 * resolution uses the WHOLE MATCH and redaction uses the CAPTURE GROUP. Every pattern carries
 * the `d` flag so `m.indices[redactGroup]` gives that span; a unit test asserts it.
 *
 * WORD BOUNDARIES. `\b` is never used where the charset contains `_` — it does not assert where
 * you expect, and a `{n,}` quantifier will backtrack to satisfy it, silently truncating the
 * redaction span. Boundaries are the explicit lookaround pair below. Lookbehind is Safari 16.4,
 * which is already the app's floor, so it costs nothing.
 */
const NB = '(?<![A-Za-z0-9_-])';
const NA = '(?![A-Za-z0-9_-])';

export interface Rule {
  readonly id: string;
  readonly label: string;
  readonly tier: Tier;
  readonly severity: Severity;
  readonly disposition: Disposition;
  /** The pseudonym type prefix, for PII rules. Empty for secrets. */
  readonly pseudonymType: string;
  readonly enabledByDefault: boolean;
  readonly pattern: RegExp;
  /** 0 = the whole match; otherwise the capture-group index that is actually replaced. */
  readonly redactGroup: number;
  readonly validate?: (m: RegExpExecArray) => ValidateResult;
  /**
   * A literal that MUST be present in a line for this rule to have any chance. Used by the
   * scanner's prefilter to skip the regex entirely. Empty means shapeless (email, the 40-char
   * AWS secret) — those are the expensive ones and there is no way around it.
   */
  readonly literals: readonly string[];
  /** Published in the preflight rule list, one line each. Honesty, surfaced not buried. */
  readonly doesNotMatch: string;
}

function rx(source: string, flags = 'gd'): RegExp {
  return new RegExp(source, flags);
}

export const RULES: readonly Rule[] = [
  // ── Tier 0 — checksum / structural validators ────────────────────────────
  {
    id: 'credit-card',
    label: 'Payment card number',
    tier: 0,
    severity: 'critical',
    disposition: 'pii',
    pseudonymType: 'CARD',
    enabledByDefault: true,
    pattern: rx(String.raw`(?<![\d-])((?:\d[ -]?){12,18}\d)(?![\d-])`),
    redactGroup: 1,
    validate: (m) => luhn(m[1] ?? ''),
    literals: [],
    doesNotMatch: 'Card numbers that fail the Luhn check digit, or shorter than 13 digits.',
  },
  {
    id: 'iban',
    label: 'IBAN bank account',
    tier: 0,
    severity: 'critical',
    disposition: 'pii',
    pseudonymType: 'IBAN',
    enabledByDefault: true,
    pattern: rx(String.raw`\b([A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]{4}){2,7}(?:[ ]?[A-Z0-9]{1,3})?)\b`),
    redactGroup: 1,
    validate: (m) => ibanMod97(m[1] ?? ''),
    literals: [],
    doesNotMatch: 'Anything failing ISO 7064 mod-97, or the wrong length for its country code.',
  },
  {
    id: 'aba-routing',
    label: 'US bank routing number',
    tier: 0,
    severity: 'high',
    disposition: 'pii',
    pseudonymType: 'ABA',
    enabledByDefault: true,
    pattern: rx(String.raw`(?:routing|aba|rtn)[^\n\r]{0,24}?(?<!\d)(\d{9})(?!\d)`, 'gdi'),
    redactGroup: 1,
    validate: (m) => abaChecksum(m[1] ?? ''),
    literals: ['routing', 'aba', 'rtn', 'ROUTING', 'ABA', 'RTN', 'Routing'],
    doesNotMatch:
      'Nine-digit numbers with no nearby "routing"/"ABA" keyword. The 3-7-1 checksum accepts about 1 in 10 random nine-digit strings.',
  },
  {
    id: 'us-ssn',
    label: 'US Social Security number',
    tier: 0,
    severity: 'critical',
    disposition: 'pii',
    pseudonymType: 'SSN',
    enabledByDefault: true,
    pattern: rx(
      String.raw`(?<![\d-])(?!000|666|9\d\d)(\d{3})-(?!00)(\d{2})-(?!0000)(\d{4})(?![\d-])`,
    ),
    redactGroup: 0,
    validate: (m) => ssnStructural(m[1] ?? '', m[2] ?? '', m[3] ?? ''),
    literals: [],
    doesNotMatch:
      'Area 000/666/900+, group 00, serial 0000. There is no checksum — the SSA randomised assignment in 2011 — so an order number like 192-83-7465 does match.',
  },

  // ── Tier 1 — published provider prefixes ─────────────────────────────────
  {
    id: 'aws-access-key-id',
    label: 'AWS access key ID',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(String.raw`(?<![A-Z0-9])((?:AKIA|ASIA|ABIA|ACCA)[A-Z0-9]{16})(?![A-Z0-9])`),
    redactGroup: 1,
    literals: ['AKIA', 'ASIA', 'ABIA', 'ACCA'],
    doesNotMatch: 'Lowercase variants, or bodies that are not exactly 16 characters.',
  },
  {
    id: 'aws-secret-access-key',
    label: 'AWS secret access key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(
      String.raw`aws[_-]?secret[_-]?access[_-]?key["'\x60]?[\s:=]{0,8}["'\x60]?([A-Za-z0-9/+=]{40})(?![A-Za-z0-9/+=])`,
      'gdi',
    ),
    redactGroup: 1,
    literals: ['aws_secret', 'aws-secret', 'awssecret', 'AWS_SECRET', 'AWS-SECRET', 'aws_Secret'],
    doesNotMatch:
      'A bare 40-character string with no nearby "aws secret access key" label — there is nothing about the shape alone to key on.',
  },
  {
    id: 'github-token',
    label: 'GitHub token',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(gh[pousr]_[A-Za-z0-9]{36,255})${NA}`),
    redactGroup: 1,
    literals: ['ghp_', 'gho_', 'ghu_', 'ghs_', 'ghr_'],
    doesNotMatch: 'Bodies shorter than 36 characters. The trailing CRC is not verified.',
  },
  {
    id: 'github-pat',
    label: 'GitHub fine-grained PAT',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(github_pat_[A-Za-z0-9]{22}_[A-Za-z0-9]{59})${NA}`),
    redactGroup: 1,
    literals: ['github_pat_'],
    doesNotMatch: 'Anything not matching the exact 22 + 59 body layout GitHub publishes.',
  },
  {
    id: 'anthropic-api-key',
    label: 'Anthropic API key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(sk-ant-api\\d{2}-[A-Za-z0-9_-]{80,120})${NA}`),
    redactGroup: 1,
    literals: ['sk-ant-api'],
    doesNotMatch: 'Bodies shorter than 80 characters.',
  },
  {
    id: 'openai-api-key',
    label: 'OpenAI API key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(
      `${NB}(sk-(?:proj|svcacct|admin)-[A-Za-z0-9_-]{20,250}|sk-[A-Za-z0-9]{20}T3BlbkFJ[A-Za-z0-9]{20})${NA}`,
    ),
    redactGroup: 1,
    literals: ['sk-proj-', 'sk-svcacct-', 'sk-admin-', 'T3BlbkFJ'],
    doesNotMatch: 'Legacy 48-character sk- keys with no project or T3BlbkFJ marker.',
  },
  {
    id: 'slack-token',
    label: 'Slack token',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(
      `${NB}(xox[baprse]-[A-Za-z0-9-]{10,250}|xapp-\\d-[A-Z0-9]{9,12}-\\d{10,13}-[a-f0-9]{60,70})${NA}`,
    ),
    redactGroup: 1,
    literals: ['xoxb-', 'xoxa-', 'xoxp-', 'xoxr-', 'xoxs-', 'xoxe-', 'xapp-'],
    doesNotMatch: 'Tokens shorter than 10 body characters.',
  },
  {
    id: 'slack-webhook',
    label: 'Slack incoming webhook',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(
      String.raw`(https://hooks\.slack\.com/services/T[A-Za-z0-9]{7,12}/B[A-Za-z0-9]{7,12}/[A-Za-z0-9]{20,32})`,
    ),
    redactGroup: 1,
    literals: ['hooks.slack.com'],
    doesNotMatch: 'Slack URLs that are not the /services/ webhook shape.',
  },
  {
    id: 'stripe-secret-key',
    label: 'Stripe live secret key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}([rs]k_live_[A-Za-z0-9]{20,247})${NA}`),
    redactGroup: 1,
    literals: ['sk_live_', 'rk_live_'],
    doesNotMatch: 'Test keys (sk_test_) and publishable keys (pk_), which are not secrets.',
  },
  {
    id: 'google-api-key',
    label: 'Google API key',
    tier: 1,
    severity: 'high',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(AIza[A-Za-z0-9_-]{35})${NA}`),
    redactGroup: 1,
    literals: ['AIza'],
    doesNotMatch: 'Anything other than AIza plus exactly 35 characters.',
  },
  {
    id: 'huggingface-token',
    label: 'Hugging Face token',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(hf_[A-Za-z0-9]{34,40})${NA}`),
    redactGroup: 1,
    literals: ['hf_'],
    doesNotMatch: 'Bodies outside 34-40 characters.',
  },
  {
    id: 'gitlab-pat',
    label: 'GitLab personal access token',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(glpat-[A-Za-z0-9_-]{20,50})${NA}`),
    redactGroup: 1,
    literals: ['glpat-'],
    doesNotMatch: 'Self-hosted GitLab tokens using a custom prefix.',
  },
  {
    id: 'npm-token',
    label: 'npm access token',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(npm_[A-Za-z0-9]{36})${NA}`),
    redactGroup: 1,
    literals: ['npm_'],
    doesNotMatch: 'Legacy UUID-shaped npm tokens, which are indistinguishable from any UUID.',
  },
  {
    id: 'sendgrid-key',
    label: 'SendGrid API key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(SG\\.[A-Za-z0-9_-]{22}\\.[A-Za-z0-9_-]{43})${NA}`),
    redactGroup: 1,
    literals: ['SG.'],
    doesNotMatch: 'Anything not matching the exact 22 + 43 two-segment layout.',
  },
  {
    id: 'gcp-service-account',
    label: 'GCP service-account private key',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(String.raw`"private_key"\s*:\s*"(-----BEGIN[^"]{100,20000}?-----END[^"]{0,60}?-----(?:\\n)?)"`),
    redactGroup: 1,
    literals: ['private_key'],
    doesNotMatch: 'A private_key field whose value is not a complete PEM block.',
  },
  {
    id: 'pem-private-key',
    label: 'PEM private key block',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    // MUST span header to footer. A header-only match redacts 30 characters of
    // "-----BEGIN EC PRIVATE KEY-----" and leaves the entire key body in the file we
    // call clean — the single worst failure this product can have.
    pattern: rx(
      String.raw`(-----BEGIN (?:[A-Z0-9 ]{0,20} )?PRIVATE KEY(?: BLOCK)?-----[\s\S]{0,20000}?-----END (?:[A-Z0-9 ]{0,20} )?PRIVATE KEY(?: BLOCK)?-----)`,
    ),
    redactGroup: 1,
    literals: ['-----BEGIN'],
    doesNotMatch: 'Public keys and certificates. Unterminated blocks are caught by the next rule.',
  },
  {
    id: 'pem-private-key-header',
    label: 'PEM private key header (unterminated)',
    tier: 1,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    // The paired regex yields zero matches on a truncated paste, so this ships too, at the same
    // severity: a half-pasted key is still a leak.
    pattern: rx(String.raw`(-----BEGIN (?:[A-Z0-9 ]{0,20} )?PRIVATE KEY(?: BLOCK)?-----)`),
    redactGroup: 1,
    literals: ['-----BEGIN'],
    doesNotMatch: 'Nothing — it is deliberately broad, and is shadowed by the complete-block rule.',
  },
  {
    id: 'mailchimp-api-key',
    label: 'Mailchimp API key',
    tier: 1,
    severity: 'high',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}([0-9a-f]{32}-us\\d{1,2})${NA}`),
    redactGroup: 1,
    literals: ['-us'],
    doesNotMatch: 'A bare 32-character hex digest with no -usN datacentre suffix.',
  },
  {
    id: 'jwt',
    label: 'JSON Web Token',
    tier: 1,
    severity: 'high',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    pattern: rx(`${NB}(eyJ[A-Za-z0-9_-]{10,4000}\\.[A-Za-z0-9_-]{10,8000}\\.[A-Za-z0-9_-]{0,2000})${NA}`),
    redactGroup: 1,
    validate: (m) => jwtDecodable(m[1] ?? ''),
    literals: ['eyJ'],
    doesNotMatch: 'Tokens whose header does not base64url-decode to JSON with an alg or typ field.',
  },

  // ── Tier 2 — structural ──────────────────────────────────────────────────
  {
    id: 'email-address',
    label: 'Email address',
    tier: 2,
    severity: 'medium',
    disposition: 'pii',
    pseudonymType: 'EMAIL',
    enabledByDefault: true,
    pattern: rx(
      String.raw`(?<![A-Za-z0-9._%+-])([A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63}){1,4})(?![A-Za-z0-9-])`,
    ),
    redactGroup: 1,
    literals: ['@'],
    doesNotMatch: 'Obfuscated forms — "alice at example dot com" survives untouched.',
  },
  {
    id: 'phone-e164',
    label: 'Phone number (E.164)',
    tier: 2,
    severity: 'medium',
    disposition: 'pii',
    pseudonymType: 'PHONE',
    enabledByDefault: true,
    pattern: rx(String.raw`(?<![\d+])(\+[1-9]\d{7,14})(?!\d)`),
    redactGroup: 1,
    literals: ['+'],
    doesNotMatch:
      'National formats without a leading +. The mandatory + is the entire precision of this rule.',
  },
  {
    id: 'url-embedded-credential',
    label: 'Password inside a URL or DSN',
    tier: 2,
    severity: 'critical',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    // Redact group 3 — the password ONLY. match[0] would destroy the scheme, the username and
    // the host, and the rewritten DSN would no longer parse.
    pattern: rx(
      String.raw`([a-z][a-z0-9+.-]{1,20}://)([^\s:@/]{1,64}):([^\s:@/]{1,128})@([A-Za-z0-9._-]{1,255})`,
      'gdi',
    ),
    redactGroup: 3,
    literals: ['://'],
    doesNotMatch: 'URLs with a username but no password, and credentials passed as query strings.',
  },
  {
    id: 'generic-secret-assignment',
    label: 'Secret assigned to a keyword',
    tier: 2,
    severity: 'high',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: true,
    // The closing quote is optional via the second alternative, because .env files are unquoted
    // by convention and "a .env with a hex secret in the corpus" is the single most likely
    // embarrassment this tool has.
    pattern: rx(
      String.raw`(?:api[_-]?key|secret(?:[_-]?access)?(?:[_-]?key)?|auth[_-]?token|access[_-]?token|passwd|password|client[_-]?secret|private[_-]?token)["'\x60]?\s*[:=]\s*(?:["'\x60]([^"'\x60\r\n]{12,256})["'\x60]|([^\s"'\x60,;)\]}]{12,256}))`,
      'gdi',
    ),
    redactGroup: 1,
    literals: [
      'api_key', 'apikey', 'api-key', 'API_KEY', 'APIKEY', 'API-KEY', 'Api_Key', 'ApiKey',
      'secret', 'SECRET', 'Secret', 'auth_token', 'AUTH_TOKEN', 'access_token', 'ACCESS_TOKEN',
      'passwd', 'PASSWD', 'password', 'PASSWORD', 'Password', 'authToken', 'accessToken',
    ],
    doesNotMatch:
      'Values under 12 characters, obvious placeholders (changeme, your-api-key-here, <redacted>), and prose like "password: please reset it".',
  },

  // ── Tier 3 — heuristics, both OFF by default ─────────────────────────────
  {
    id: 'ipv4',
    label: 'Public IPv4 address',
    tier: 3,
    severity: 'info',
    disposition: 'pii',
    pseudonymType: 'IPV4',
    enabledByDefault: false,
    pattern: rx(String.raw`(?<![\w.])((?:\d{1,3}\.){3}\d{1,3})(?![\d.])`),
    redactGroup: 1,
    literals: ['.'],
    doesNotMatch:
      'Private, loopback and documentation ranges. But a bare four-part build number like 8.4.12.9 does match, which is why this ships switched off.',
  },
  {
    id: 'entropy-orphan',
    label: 'High-entropy string (heuristic)',
    tier: 3,
    severity: 'info',
    disposition: 'secret',
    pseudonymType: '',
    enabledByDefault: false,
    // Handled by src/core/entropy.ts, not by this pattern — the pattern exists so the rule has
    // a uniform shape in the registry and in the UI. It is never executed.
    pattern: rx(String.raw`(?!)x`),
    redactGroup: 0,
    literals: [],
    doesNotMatch:
      'Digests, UUIDs, base64 media, file paths, semvers, timestamps and English prose. It also misses about 4.4% of real 20-character random secrets, which is the price of that.',
  },
];

export const RULE_BY_ID: Readonly<Record<string, Rule>> = Object.freeze(
  Object.fromEntries(RULES.map((r) => [r.id, r])),
);

export const ENABLED_BY_DEFAULT: readonly string[] = RULES.filter((r) => r.enabledByDefault).map(
  (r) => r.id,
);

/**
 * Every rule count printed anywhere in the UI comes from here. Hand-typing one is a correctness
 * bug, not a style issue: it is a safety claim the user acts on by uploading four gigabytes.
 */
export const COUNTS = {
  total: RULES.length,
  enabled: ENABLED_BY_DEFAULT.length,
  checksum: RULES.filter((r) => r.tier === 0).length,
  prefix: RULES.filter((r) => r.tier === 1).length,
  structural: RULES.filter((r) => r.tier === 2).length,
  heuristic: RULES.filter((r) => r.tier === 3).length,
} as const;

/** Placeholder values that look like secrets but are documentation. */
const PLACEHOLDER_RE =
  /^(?:your[-_ ]|changeme|change[-_ ]me|replace[-_ ]?me|x{3,}|<[^>]+>|\.\.\.|example|placeholder|todo|dummy|sample|test[-_ ]?key|insert[-_ ]|redacted|\*{3,}|\$\{|%\(|\{\{)/i;

export function isPlaceholderValue(v: string): boolean {
  if (PLACEHOLDER_RE.test(v.trim())) return true;
  if (/^[-_. ]*$/.test(v)) return true;
  return false;
}
